<template>
  <div class="kanban-screen">
    <div class="search-container">
      <div class="search-filters-kanban">
        <div class="search-input-wrapper-kanban">
          <input
            type="text"
            v-model="searchTerm"
            placeholder="Filtrar por nome ou telefone"
            class="search-kanban-input"
            :disabled="isLoadingLeads"
          />

          <svg
            class="search-icon"
            width="19"
            height="19"
            viewBox="0 0 19 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17.7539 15.5742C18.0703 15.9258 18.0703 16.4531 17.7188 16.7695L16.7344 17.7539C16.418 18.1055 15.8906 18.1055 15.5391 17.7539L12.0586 14.2734C11.8828 14.0977 11.8125 13.8867 11.8125 13.6758V13.0781C10.5469 14.0625 9 14.625 7.3125 14.625C3.26953 14.625 0 11.3555 0 7.3125C0 3.30469 3.26953 0 7.3125 0C11.3203 0 14.625 3.30469 14.625 7.3125C14.625 9.03516 14.0273 10.582 13.0781 11.8125H13.6406C13.8516 11.8125 14.0625 11.918 14.2383 12.0586L17.7539 15.5742ZM7.3125 11.8125C9.77344 11.8125 11.8125 9.80859 11.8125 7.3125C11.8125 4.85156 9.77344 2.8125 7.3125 2.8125C4.81641 2.8125 2.8125 4.85156 2.8125 7.3125C2.8125 9.80859 4.81641 11.8125 7.3125 11.8125Z"
              fill="#E2211C"
            />
          </svg>
        </div>

        <div class="priority-filter">
          <span class="filter-title">Prioridade:</span>
          <span
            v-for="color in ['green', 'yellow', 'red']"
            :key="color"
            class="priority-dot"
            :class="color"
            :style="{
              cursor: 'pointer',
              marginLeft: '8px',
              border:
                selectedPriority === color
                  ? '2px solid black'
                  : '1px solid #ccc',
            }"
            @click="togglePriorityFilter(color)"
            :title="'Filtrar por prioridade: ' + color"
          ></span>
        </div>
      </div>
    </div>

    <div class="kanban">
      <div
        v-for="(column, columnIndex) in filteredColumns"
        :key="columnIndex"
        class="kanban-column"
        @dragover="onDragOver"
        @drop="onDrop($event, columnIndex)"
      >
        <div class="kanban-title">
          <h2 class="kanban-title-text">
            <span class="kanban-title-icon">
              <svg
                v-if="column.icon === 'atendimento_iniciado'"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <polygon
                  points="10,8 16,12 10,16"
                  fill="currentColor"
                  stroke="none"
                />
              </svg>

              <svg
                v-else-if="column.icon === 'resposta'"
                width="27"
                height="29"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="4" y="5" width="16" height="13" rx="2" ry="2" />
                <path d="M11 8L8 11L11 14" />
                <line x1="8" y1="11" x2="16" y2="11" />
              </svg>

              <svg
                v-else-if="column.icon === 'negociacao'"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="4" y1="7" x2="14" y2="7" />
                <polyline points="11,4 14,7 11,10" />

                <line x1="20" y1="17" x2="10" y2="17" />
                <polyline points="13,14 10,17 13,20" />
              </svg>

              <svg
                v-else-if="column.icon === 'concluido'"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <polyline points="8,12 11,15 17,9" />
              </svg>
            </span>

            {{ column.title }}
            <span v-if="isLoadingLeads" class="count-skeleton"></span>
            <template v-else>({{ getColumnCount(column.title) }})</template>
          </h2>
        </div>

        <div class="kanban-cards">
          <template v-if="isLoadingLeads">
            <div
              v-for="n in 3"
              :key="n"
              class="kanban-card skeleton-card"
              aria-hidden="true"
            >
              <div class="kanban-card-header">
                <div class="skeleton-avatar shimmer"></div>
                <div class="contact-info">
                  <div
                    class="skeleton-line shimmer"
                    style="width: 160px; height: 14px"
                  ></div>
                  <div
                    class="skeleton-line shimmer"
                    style="width: 90px; height: 10px; margin-top: 6px"
                  ></div>
                </div>
              </div>
              <div class="kanban-card-content">
                <div
                  class="skeleton-line shimmer"
                  style="width: 80%; height: 12px"
                ></div>
                <div
                  class="skeleton-line shimmer"
                  style="width: 40%; height: 12px; margin-top: 6px"
                ></div>
              </div>
            </div>
          </template>

          <template v-else>
            <div
              v-for="(card, index) in column.cards"
              :key="index"
              class="kanban-card"
              :style="{ borderLeft: `4px solid ${card.borderLeftColor}` }"
              draggable="true"
              @dragstart="onDragStart($event, card, columnIndex)"
              @dragend="onDragEnd"
              @click="openChat(card)"
            >
              <div class="kanban-card-header">
                <img
                  :src="resolveProfilePicture(card)"
                  alt="Foto do Contato"
                  class="contact-photo"
                  @error="handleImageError(card)"
                />
                <div class="contact-info">
                  <div class="contact-name-priority">
                    <strong>{{ getCardTitle(card) }}</strong>
                    <div
                      class="priority-dot"
                      :class="card.priorityColor"
                      @click.stop="cyclePriority(card)"
                      title="Clique para mudar prioridade"
                    ></div>
                  </div>
                </div>
              </div>

              <div class="kanban-card-content">
                <div class="last-message-container">
                  <div class="last-message incoming">
                    {{ card.lastMessage || "Sem mensagens recentes" }}
                  </div>

                  <div class="message-time">
                    {{ card.lastMessageTime }}
                    <span
                      v-if="shouldShowAck(column.title, card)"
                      class="ack"
                      :class="'ack-' + (card.ack ?? 0)"
                      :title="ackTitle(card.ack)"
                    >
                      {{ ackGlyph(card.ack) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!column.cards.length" class="empty-column">
              Nenhum card aqui ainda.
            </div>
          </template>
        </div>
      </div>
    </div>

    <ChatWindow
      ref="chatWindow"
      v-if="showChat"
      :phoneNumber="selectedPhone"
      :contactName="selectedName"
      :visible="showChat"
      :socket="socket"
      @close="showChat = false"
      @update-last-message="updateLastMessage"
    />
  </div>
  <WhatsappBlocker v-if="!whatsappAutenticado" />
</template>

<script>
import ChatWindow from "@/components/messages/ChatWindow.vue";
import { getAuth } from "firebase/auth";
import { io } from "socket.io-client";
import { secureFetch } from "@/main.js";
import { Chart, registerables } from "chart.js";
import WhatsappBlocker from "@/components/WhatsappBlocker.vue";
import { logout } from "@/utils/sessionUtils";

function storageKey(key, uid) {
  return `magleads:${uid}:${key}`;
}
async function getUid() {
  const auth = getAuth();
  const user = auth.currentUser;
  return user?.uid || "anonymous";
}

const apiBaseUrl =
  process.env.VUE_APP_API_BASE_URL || "https://177.36.4.221:3000";
const socketUrl = process.env.VUE_APP_SOCKET_URL || window.location.origin;

Chart.register(...registerables);

export default {
  components: { ChatWindow, WhatsappBlocker },
  props: {
    preparedContacts: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["prepare-send", "add-card-to-kanban", "update-metrics"],
  data() {
    return {
      showChat: false,
      searchTerm: "",
      selectedPhone: "",
      selectedName: "",
      selectedPriority: null,
      socket: null,
      draggedCard: null,
      draggedFromColumn: null,
      isLoadingLeads: true,
      waReadyFlag: false,
      uidCache: null,
      email_ini: null,
      kanbanColumns: [
        {
          title: "ATENDIMENTO INICIADO",
          cards: [],
          icon: "atendimento_iniciado",
        },
        {
          title: "RESPOSTA DO CLIENTE",
          cards: [],
          icon: "resposta",
        },
        {
          title: "EM NEGOCIAÇÃO",
          cards: [],
          icon: "negociacao",
        },
        {
          title: "CONCLUÍDO",
          cards: [],
          icon: "concluido",
        },
      ],
    };
  },

  async mounted() {
    this.uidCache = await getUid();
    window.addEventListener("storage", this.onStorageChange);
    await this.ensureWhatsAppReady();

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      let token = null;
      let email = null;

      if (user) {
        token = await user.getIdToken(true);
        email = user.email || null;
      } else {
        token = localStorage.getItem("userToken") || null;
        email =
          localStorage.getItem("email_ini") ||
          localStorage.getItem("ssoEmail") ||
          null;
      }

      this.email_ini = email;

      if (!token) {
        console.error(
          "🔴 Nenhum token disponível (Firebase ou SSO). WebSocket não será iniciado."
        );
      } else {
        //this.socket = io(`${apiBaseUrl}`, { auth: { token } });

        this.socket = io(socketUrl, {
          auth: { token },
          transports: ["websocket"], // ✅ não usa polling
          upgrade: false,            // ✅ não tenta “upgrade”
        });

        this.socket.on("connect", () =>
          console.log("🔗 Conectado ao WebSocket!")
        );
        this.socket.on("disconnect", () =>
          console.log("⚠️ Desconectado do WebSocket.")
        );

        this.socket.on("new-customer-response", (data) => {
          if (data.phone.endsWith("@g.us")) return;
          if (this.selectedPhone === data.phone && this.showChat) {
            this.$refs.chatWindow?.receiveMessageFromWebSocket(data);
          }
          this.handleCustomerResponse(data.phone, data.message);
        });

        const handleAckUpdate = ({ phone, ack, status, msgId, id }) => {
          const finalAck = typeof status === "number" ? status : ack;
          if (finalAck === undefined || finalAck === null) return;

          const finalMsgId = msgId || id || null;

          // 1) Atualiza card (se tiver phone)
          if (phone) {
            const card = this.findCardByPhone(phone);
            if (card) {
              card.ack = finalAck;
              this.$forceUpdate();
            }
          }

          // 2) Atualiza o chat aberto por msgId
          const chat = this.$refs.chatWindow;
          if (chat && Array.isArray(chat.messages)) {
            const msgs = chat.messages;

            // se veio msgId, atualiza a msg exata
            if (finalMsgId) {
              const m = msgs.find(x => (x.msgId || x.id) === finalMsgId);
              if (m) {
                m.ack = finalAck;
                chat.$forceUpdate?.();
                return;
              }
            }

            // fallback: seu comportamento atual (última enviada)
            if (this.showChat && this.selectedPhone === phone) {
              for (let i = msgs.length - 1; i >= 0; i--) {
                if (msgs[i].type === "sent") {
                  msgs[i].ack = finalAck;
                  break;
                }
              }
              chat.$forceUpdate?.();
            }
          }
        };

        this.socket.on("message-ack", handleAckUpdate);
        this.socket.on("message-status-update", handleAckUpdate);

        this.socket.on("message-reaction", ({ phone, reactions }) => {
          const card = this.findCardByPhone(phone);
          if (card && reactions?.length) {
            const first = reactions[0];
            card.lastMessage = `${card.lastMessage || ""}  ${
              first?.emoji || "👍"
            }`;
            this.$forceUpdate();
          }
        });

        this.socket.on("message-revoke", ({ phone }) => {
          const card = this.findCardByPhone(phone);
          if (card) {
            card.lastMessage = "(mensagem apagada)";
            this.$forceUpdate();
          }
        });

        this.socket.on("update-last-message", ({ phone, message }) => {
          this.updateLastMessage({ phone, message });
        });
      }
    } catch (e) {
      console.error("❌ Erro ao conectar WebSocket:", e);
    }

    await this.fetchRecentLeads();
    this.addPreparedContactsToKanban();
  },

  watch: {
    preparedContacts: {
      immediate: true,
      deep: true,
      handler() {
        this.addPreparedContactsToKanban();
      },
    },
  },

  beforeUnmount() {
    window.removeEventListener("storage", this.onStorageChange);
    if (this.socket) {
      this.socket.off("connect");
      this.socket.off("disconnect");
      this.socket.off("new-customer-response");
      this.socket.off("message-ack");
      this.socket.off("message-status-update");
      this.socket.off("message-reaction");
      this.socket.off("message-revoke");
      this.socket.removeAllListeners();
      this.socket.close();
      //this.socket.disconnect();
      this.socket = null;
    }
  },

  computed: {
    whatsappAutenticado() {
      const uid = this.uidCache || "anonymous";
      const a =
        localStorage.getItem(storageKey("acessoComWhatsapp", uid)) === "true";
      const q =
        localStorage.getItem(storageKey("qrCodeScanned", uid)) === "true";

      const aLegacy = localStorage.getItem("acessoComWhatsapp") === "true";
      const qLegacy = localStorage.getItem("qrCodeScanned") === "true";

      return (a && q) || (aLegacy && qLegacy) || this.waReadyFlag === true;
    },
    filteredColumns() {
      const term = this.searchTerm.toLowerCase();
      return this.kanbanColumns.map((col) => ({
        ...col,
        cards: col.cards.filter((card) => {
          const matchTerm =
            (card.name && card.name.toLowerCase().includes(term)) ||
            (card.phone && card.phone.toLowerCase().includes(term));
          const matchPriority =
            !this.selectedPriority ||
            card.priorityColor === this.selectedPriority;
          return matchTerm && matchPriority;
        }),
      }));
    },
  },

  methods: {
    // 🔹 helper para pegar e-mail do usuário (Firebase OU SSO)
    async resolveUserEmail() {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user?.email) return user.email;

      return (
        localStorage.getItem("email_ini") ||
        localStorage.getItem("ssoEmail") ||
        null
      );
    },
    getCardTitle(card) {
      // tenta várias possibilidades de nome
      const candidates = [
        card.contactName,
        card.name,
        card.lead?.contactName,
        card.lead?.name,
      ];

      const candidate =
        candidates
          .map((v) => (v != null ? String(v).trim() : ""))
          .find((v) => v.length > 0) ||
        (card.phone ? String(card.phone).trim() : "") ||
        "Cliente";

      return candidate;
    },
    onStorageChange(e) {
      if (
        e.key?.includes("magleads:") ||
        e.key === "qrCodeScanned" ||
        e.key === "acessoComWhatsapp"
      ) {
        const uid = this.uidCache || "anonymous";
        const a =
          localStorage.getItem(storageKey("acessoComWhatsapp", uid)) === "true";
        const q =
          localStorage.getItem(storageKey("qrCodeScanned", uid)) === "true";
        this.waReadyFlag = a && q;
        this.$forceUpdate();
      }
    },

    async ensureWhatsAppReady() {
      try {
        const resp = await secureFetch(`${apiBaseUrl}/whatsapp-status`, {
          method: "GET",
        });
        const data = await resp.json();
        const uid = this.uidCache || (await getUid());

        if (data.status === "ready") {
          localStorage.setItem(storageKey("qrCodeScanned", uid), "true");
          localStorage.setItem(storageKey("acessoComWhatsapp", uid), "true");
          localStorage.removeItem("qrCodeScanned");
          localStorage.removeItem("acessoComWhatsapp");
          this.waReadyFlag = true;
        } else {
          localStorage.setItem(storageKey("qrCodeScanned", uid), "false");
          localStorage.setItem(storageKey("acessoComWhatsapp", uid), "false");
          this.waReadyFlag = false;
        }
      } catch (e) {
        console.warn("ensureWhatsAppReady falhou:", e.message);
      }
    },

    async getMessagesOrdered(telefoneCliente) {
      try {
        const userEmail = await this.resolveUserEmail();
        if (!userEmail) return [];

        const resp = await secureFetch(
          `${apiBaseUrl}/get-messages?telefoneCliente=${encodeURIComponent(
            telefoneCliente
          )}&userEmail=${encodeURIComponent(userEmail)}`,
          { method: "GET" }
        );

        const data = await resp.json().catch(() => []);
        const msgs = Array.isArray(data) ? data : [];

        return msgs.map((m) => ({
          tipoMsg: m.type === "received" ? "Cliente" : "Vendedor",
          time: m.time || m.timestamp || m.date || m.createdAt || null,
        }));
      } catch (e) {
        console.warn("⚠️ Falha ao obter mensagens ordenadas:", e);
        return [];
      }
    },

    classifyStageByIndex(messagesInOrder) {
      const firstClientIndex = messagesInOrder.findIndex(
        (m) => m.tipoMsg === "Cliente"
      );
      if (firstClientIndex === -1) {
        return {
          stage: "ATENDIMENTO INICIADO",
          lock: false,
          lastFrom: messagesInOrder.at(-1)?.tipoMsg || null,
        };
      }

      const vendorAfter = messagesInOrder.some(
        (m, idx) => m.tipoMsg === "Vendedor" && idx > firstClientIndex
      );

      if (vendorAfter) {
        return {
          stage: "EM NEGOCIAÇÃO",
          lock: true,
          lastFrom: messagesInOrder.at(-1)?.tipoMsg || null,
        };
      }
      return {
        stage: "RESPOSTA DO CLIENTE",
        lock: false,
        lastFrom: messagesInOrder.at(-1)?.tipoMsg || null,
      };
    },

    /* ========= Acks UI ========= */
    shouldShowAck(columnTitle, card) {
      return (
        card.lastFrom === "Vendedor" || columnTitle === "ATENDIMENTO INICIADO"
      );
    },
    ackGlyph(ack) {
      switch (ack) {
        case 1:
          return "✔";
        case 2:
          return "✔✔";
        case 3:
          return "✔✔";
        case 4:
          return "▶︎✔✔";
        default:
          return "⌛";
      }
    },
    ackTitle(ack) {
      return (
        {
          0: "Pendente",
          1: "Enviado ao servidor",
          2: "Entregue",
          3: "Lido",
          4: "Reproduzido",
        }[ack ?? 0] || "Pendente"
      );
    },

    /* ========= Filtros / prioridade ========= */
    togglePriorityFilter(color) {
      this.selectedPriority = this.selectedPriority === color ? null : color;
    },
    async cyclePriority(card) {
      const colors = ["green", "yellow", "red"];
      const idx = colors.indexOf(card.priorityColor);
      card.priorityColor = colors[(idx + 1) % colors.length];
      await this.updatePriorityInDatabase(card.phone, card.priorityColor);
    },
    async updatePriorityInDatabase(phone, priorityColor) {
      try {
        const resp = await secureFetch(`${apiBaseUrl}/atualizar-prioridade`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ telefoneCliente: phone, novaPrioridade: priorityColor }),
        });
        if (!resp.ok) throw new Error("Erro ao salvar prioridade no banco.");
      } catch (e) {
        console.error("❌ Erro ao atualizar prioridade:", e);
      }
    },

    /* ========= Básico UI ========= */
    getColumnCount(columnName) {
      const column = this.kanbanColumns.find((col) => col.title === columnName);
      return column ? column.cards.length : 0;
    },
    openChat(card) {
      this.selectedPhone = card.phone;
      this.selectedName = this.getCardTitle(card);
      this.showChat = true;
    },

    /* ========= Drag & Drop ========= */
    onDragStart(e, card, columnIndex) {
      this.draggedCard = card;
      this.draggedFromColumn = columnIndex;
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", card.phone);
    },
    onDragEnd() {
      this.draggedCard = null;
      this.draggedFromColumn = null;
    },
    onDragOver(e) {
      e.preventDefault();
    },
    async onDrop(e, targetColumnIndex) {
      e.preventDefault();
      const phone = e.dataTransfer.getData("text/plain");
      if (!phone || !this.draggedCard) return;

      const card = this.draggedCard;
      const targetColumn = this.kanbanColumns[targetColumnIndex];
      if (!targetColumn) return;

      this.kanbanColumns[this.draggedFromColumn].cards = this.kanbanColumns[
        this.draggedFromColumn
      ].cards.filter((c) => c !== card);

      if (!targetColumn.cards.some((c) => c.phone === card.phone)) {
        card.borderLeftColor = this.getColumnBorderColor(targetColumn.title);
        targetColumn.cards.push(card);
      }

      this.draggedCard = null;
      this.draggedFromColumn = null;
      this.$forceUpdate();
      await this.updateCardColumnInDatabase(phone, targetColumn.title);
    },

    /* ========= Tempo real ========= */
    async handleCustomerResponse(phone, newMessage) {
      const sourceColumn = this.kanbanColumns.find((col) =>
          col.cards.some((c) => c.phone === phone)
      );

      if (!sourceColumn) return;
      const idx = sourceColumn.cards.findIndex((c) => c.phone === phone);


      if (idx === -1) return;

      const card = sourceColumn.cards[idx];

      card.lastMessage = newMessage;
      card.lastFrom = "Cliente";
      card.ack = null;
      card.clientResponses = (card.clientResponses || 0) + 1;

      console.log("CARD ATUALIZADO:", card);

      const destino = card.lockNegotiation
          ? "EM NEGOCIAÇÃO"
          : "RESPOSTA DO CLIENTE";

      await this.moveToColumn(phone, destino);
  },

    async getDbCounts(telefoneCliente) {
      try {
        const userEmail = await this.resolveUserEmail();
        if (!userEmail) return { vendor: 0, client: 0 };

        const resp = await secureFetch(
          `${apiBaseUrl}/get-messages?telefoneCliente=${encodeURIComponent(
            telefoneCliente
          )}&userEmail=${encodeURIComponent(userEmail)}`,
          { method: "GET" }
        );

        const data = await resp.json().catch(() => []);
        const msgs = Array.isArray(data) ? data : [];

        return {
          vendor: msgs.filter((m) => m.type === "sent").length,
          client: msgs.filter((m) => m.type === "received").length,
        };
      } catch (e) {
        console.warn("⚠️ Falha ao obter contadores do DB:", e);
        return { vendor: 0, client: 0 };
      }
    },

    async updateLastMessage({ phone, message, ack }) {
      let shouldMove = false;

      this.kanbanColumns.forEach((column) => {
        column.cards.forEach((card) => {
          if (card.phone === phone && message?.trim()) {
            card.lastMessage = message;
            card.lastMessageTime = new Date().toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
            });
            card.lastFrom = "Vendedor";
            if (typeof ack === "number") card.ack = ack;
          }
        });
      });

      this.kanbanColumns.forEach((column) => {
        column.cards.forEach((card) => {
          if (card.phone !== phone) return;
          card.vendorMessages = (card.vendorMessages || 0) + 1;
          localStorage.setItem(`vm:${card.phone}`, String(card.vendorMessages));

          if ((card.clientResponses || 0) >= 1 && !card.lockNegotiation) {
            card.lockNegotiation = true;
            shouldMove = true;
          }
        });
      });

      if (shouldMove) await this.moveToColumn(phone, "EM NEGOCIAÇÃO");
      this.$forceUpdate();
    },

    /* ========= Mover & persistir ========= */
    async moveToColumn(phone, targetColumnName) {
      const fromColumn = this.kanbanColumns.find((c) =>
        c.cards.some((card) => card.phone === phone)
      );
      if (!fromColumn) return;

      const idx = fromColumn.cards.findIndex((c) => c.phone === phone);
      if (idx === -1) return;

      const [card] = fromColumn.cards.splice(idx, 1);
      const toColumn = this.kanbanColumns.find(
        (c) => c.title === targetColumnName
      );
      if (!toColumn) return;

      if (!toColumn.cards.some((c) => c.phone === card.phone)) {
        card.borderLeftColor = this.getColumnBorderColor(targetColumnName);
        toColumn.cards.push(card);
      }

      this.$forceUpdate();
      await this.updateCardColumnInDatabase(phone, targetColumnName);
    },

    async updateCardColumnInDatabase(phone, column) {
      try {
        const resp = await secureFetch(`${apiBaseUrl}/atualizar-coluna-card`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ telefoneCliente: phone, novaColuna: column }),
        });
        if (!resp.ok) {
          const error = await resp.json().catch(() => ({}));

          if (error.code === "WHATSAPP_SESSION_EXPIRED") {
            alert("Sessão do whatsapp expirada. Faça login novamente.");

            logout();
            return;
          }
        }
      } catch (e) {
        console.error("❌ Erro ao atualizar a coluna do card:", e);
      }
    },

    /* ========= Utilidades ========= */
    findCardByPhone(phone) {
      for (const column of this.kanbanColumns) {
        const foundCard = column.cards.find((card) => card.phone === phone);
        if (foundCard) return foundCard;
      }
      return null;
    },

    async fetchProfileImages(cards) {
      for (const card of cards)
        card.profilePicture = await this.getProfileImage(card.profilePicture);
    },

    async getProfileImage(url) {
      try {
        if (!url || !url.startsWith("https://"))
          return "https://ui-avatars.com/api/?name=Contato&background=random";

        const resp = await secureFetch(
          `${apiBaseUrl}/proxy-image?url=${encodeURIComponent(url)}`
        );
        if (!resp || !resp.ok)
          return "https://ui-avatars.com/api/?name=Contato&background=random";
        return `${apiBaseUrl}/proxy-image?url=${encodeURIComponent(url)}`;
      } catch {
        return "https://ui-avatars.com/api/?name=Contato&background=random";
      }
    },

    /* ========= Carregar leads ========= */
    async fetchRecentLeads() {
      this.isLoadingLeads = true;

      try {
        const email = await this.resolveUserEmail();

        if (!email) {
          console.warn("⚠️ Nenhum e-mail (Firebase ou SSO) ao carregar leads.");
          this.isLoadingLeads = false;
          return;
        }

        const resp = await secureFetch(
          `${apiBaseUrl}/get-recent-leads?email=${encodeURIComponent(email)}`,
          { method: "GET" }
        );

        if (!resp) {
          console.error(
            "❌ secureFetch não retornou response em get-recent-leads."
          );
          return;
        }

        if (!resp.ok) {
          const error = await resp.json().catch(() => ({}));

          console.error(`ERRO - ${resp.status}`, error);

          if (error.code === "WHATSAPP_SESSION_EXPIRED") {
            alert("Sessão do whatsapp expirada. Faça login novamente.");

            logout();
            return;
          }
          return;
        }

        const data = await resp.json().catch(() => ({}));

        console.log("📥 Dados brutos de get-recent-leads:", data);

        this.kanbanColumns.forEach((c) => (c.cards = []));

        const allColumns = Object.keys(data || {});

        for (const columnName of allColumns) {
          const leads = data[columnName];
          if (!Array.isArray(leads)) continue;

          const enriched = await Promise.all(
            leads.map(async (lead) => {
              const { vendor, client } = await this.getDbCounts(lead.phone);
              const ordered = await this.getMessagesOrdered(lead.phone);
              const { stage, lock, lastFrom } =
                this.classifyStageByIndex(ordered);

              return {
                lead,
                vendor,
                client,
                initialColumn: stage,
                lastFrom,
                lock,
              };
            })
          );

          for (const item of enriched) {
            const { lead, vendor, client, initialColumn, lastFrom, lock } = item;

            const contactNameFromBackend =
              lead.contactName ||
              lead.name ||
              lead.pushName ||
              lead.displayName ||
              lead.nome_contato ||
              null;

            this.addCardToKanban(
              initialColumn,
              contactNameFromBackend,
              lead.phone,
              lead.lastMessage,
              lead.lastMessageTime,
              lead.profilePicture,
              lead.priorityColor,
              {
                dbVendor: vendor,
                dbClient: client,
                lock,
                lastFrom,
                contactName: contactNameFromBackend,
              }
            );
          }
        }

        this.computeDashboardMetrics();
      } catch (e) {
        console.error("❌ Erro ao buscar leads recentes:", e);
      } finally {
        this.isLoadingLeads = false;
      }
    },

    addPreparedContactsToKanban() {
      if (!Array.isArray(this.preparedContacts)) return;

      this.preparedContacts.forEach((contact) => {
        if (!contact || !contact.phone) return;

        const jaExiste = this.findCardByPhone(contact.phone);
        if (jaExiste) return;

        this.addCardToKanban(
          contact.columnName || "ATENDIMENTO INICIADO",
          contact.name || "Cliente",
          contact.phone,
          contact.lastMessage || "",
          contact.lastMessageTime ||
            new Date().toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
            }),
          contact.profilePicture || null,
          contact.priorityColor || "cinza"
        );
      });
    },

    addCardToKanban(
      columnName,
      name,
      phone,
      lastMessage,
      lastMessageTime,
      profilePicture,
      priorityColor,
      opts = { dbVendor: 0, dbClient: 0, lock: false, lastFrom: null, contactName: null }
    ) {
      priorityColor = priorityColor || "green";

      // prioriza o nome real vindo do backend (contactName), depois o name
      const rawName =
        (opts && opts.contactName && String(opts.contactName).trim()) ||
        (name && String(name).trim()) ||
        (phone && String(phone).trim()) ||
        "";

      const safeName = rawName || "Cliente";

      if (
        !profilePicture ||
        typeof profilePicture !== "string" ||
        !profilePicture.startsWith("http")
      ) {
        profilePicture = `https://ui-avatars.com/api/?name=${encodeURIComponent(
          safeName
        )}`;
      }

      const persistedVM = Number(localStorage.getItem(`vm:${phone}`) || "0");
      const vendorMessages = Math.max(Number(opts.dbVendor || 0), persistedVM);

      const card = {
        name: safeName,
        phone,
        lastMessage,
        lastMessageTime,
        profilePicture,
        userEmail: this.email_ini,
        priorityColor,
        borderLeftColor: this.getColumnBorderColor(columnName),
        clientResponses: Number(opts.dbClient || 0),
        vendorMessages,
        lockNegotiation: Boolean(opts.lock),
        lastFrom: opts.lastFrom || undefined,
        contactName: opts.contactName || safeName, 
        ack: null,
      };

      let column = this.kanbanColumns.find((col) => col.title === columnName);
      if (!column)
        column = this.kanbanColumns.find(
          (col) => col.title === "ATENDIMENTO INICIADO"
        );
      column?.cards.push(card);
    },

    computeDashboardMetrics() {
      const userEmail = this.email_ini;
      let totalMessagesSent = 0,
        totalResponses = 0,
        responseTimes = [],
        totalConversions = 0;

      this.kanbanColumns.forEach((column) => {
        column.cards.forEach((card) => {
          if (card.userEmail === userEmail) {
            if (column.title === "ATENDIMENTO INICIADO") totalMessagesSent++;
            if (column.title === "RESPOSTA DO CLIENTE") {
              totalResponses++;
              if (card.firstResponseTime && card.messageSentTime) {
                const responseTime =
                  (new Date(card.firstResponseTime) -
                    new Date(card.messageSentTime)) /
                  60000;
                responseTimes.push(responseTime);
              }
            }
            if (column.title === "CONCLUÍDO") totalConversions++;
          }
        });
      });

      const responseRate =
        totalMessagesSent > 0 ? (totalResponses / totalMessagesSent) * 100 : 0;
      const avgResponseTime =
        responseTimes.length > 0
          ? responseTimes.reduce((s, t) => s + t, 0) / responseTimes.length
          : 0;
      const conversionRate =
        totalMessagesSent > 0
          ? (totalConversions / totalMessagesSent) * 100
          : 0;

      this.$emit("update-metrics", {
        responseRate,
        avgResponseTime,
        conversionRate,
        totalMessagesSent,
      });
    },

    getColumnBorderColor(columnName) {
      const colors = {
        "ATENDIMENTO INICIADO": "#d83c37",
        "RESPOSTA DO CLIENTE": "#eea237",
        "EM NEGOCIAÇÃO": "#3ab8df",
        CONCLUÍDO: "#3db83d",
      };
      return colors[columnName] || "#ccc";
    },
    resolveProfilePicture(card) {
      return card.profilePicture?.trim()?.startsWith("http")
        ? `${card.profilePicture}?t=${new Date().getTime()}`
        : `https://ui-avatars.com/api/?name=${encodeURIComponent(card.name)}`;
    },
    handleImageError(card) {
      card.profilePicture = `https://ui-avatars.com/api/?name=${encodeURIComponent(
        card.name
      )}`;
      this.$forceUpdate();
    },
  },
};
</script>

<style src="@/assets/styles.css">
.search-input-wrapper {
  position: relative;
  display: inline-block;
}

.search-input-wrapper .search-kanban-input {
  padding-left: 32px; /* espaço pro ícone */
}

.search-input-wrapper .search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none; /* não atrapalhar o clique no input */
}
</style>
